/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type SoapInput } from '@therapy/shared-validators';
import { type JSX } from 'react';
import { type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface SoapFieldsProps {
  readonly register: UseFormRegister<SoapInput>;
  readonly errors: FieldErrors<SoapInput>;
  readonly disabled: boolean;
}

const SECTIONS: readonly (keyof SoapInput)[] = ['subjective', 'objective', 'assessment', 'plan'];

/** The four SOAP textareas, registered with the parent RHF form. */
export function SoapFields({ register, errors, disabled }: SoapFieldsProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {SECTIONS.map((section) => {
        const error = errors[section];
        return (
          <div key={section}>
            <label
              htmlFor={`soap-${section}`}
              className="mb-1.5 block text-sm font-medium text-slate-700"
            >
              {t(`therapist.note.${section}`)}
            </label>
            <textarea
              id={`soap-${section}`}
              rows={3}
              disabled={disabled}
              aria-invalid={error !== undefined}
              className="focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm disabled:opacity-70"
              {...register(section)}
            />
            {error !== undefined && (
              <p role="alert" className="mt-1 text-xs text-red-600">
                {error.message}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
